import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Award, Heart, ArrowRight } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const team = [
    {
      name: 'Sarah Johnson',
      role: 'Founder & CEO',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop',
      bio: 'Former product manager at leading tech companies with 10+ years of experience.'
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
      bio: 'Engineering leader passionate about building scalable platforms.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Content',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop',
      bio: 'Content strategist helping businesses make informed software decisions.'
    },
    {
      name: 'David Kim',
      role: 'Head of Partnerships',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop',
      bio: 'Building relationships with software vendors worldwide.'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Transparency',
      description: 'We provide honest, unbiased reviews to help you make the best decisions.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Our community of users drives everything we do.'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'We thoroughly vet every tool before adding it to our platform.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: "We're passionate about helping businesses succeed with the right tools."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            About ToolsMetric
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            We're on a mission to help businesses discover and choose the perfect software tools. 
            Our platform connects you with honest reviews, detailed comparisons, and expert insights.
          </p>
          <div className="flex justify-center gap-4">
            <Button asChild className="bg-emerald-500 hover:bg-emerald-600">
              <Link to="/tools">Browse Tools</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-emerald-600 mb-2">2,500+</div>
                <div className="text-gray-600">Tools Listed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-emerald-600 mb-2">50+</div>
                <div className="text-gray-600">Categories</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-emerald-600 mb-2">45K+</div>
                <div className="text-gray-600">User Reviews</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-emerald-600 mb-2">150K+</div>
                <div className="text-gray-600">Monthly Visitors</div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                ToolsMetric was founded in 2023 with a simple goal: make it easier for businesses 
                to find the right software tools without spending weeks on research.
              </p>
              <p className="text-gray-600 mb-4">
                We noticed that choosing business software had become overwhelming. With thousands 
                of options available, teams were wasting valuable time comparing features, pricing, 
                and reading scattered reviews.
              </p>
              <p className="text-gray-600">
                Today, we're proud to be the go-to resource for businesses looking to make informed 
                software decisions. Our platform combines comprehensive data, real user reviews, and 
                expert analysis to help you find your perfect tools.
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-emerald-100 text-lg">
                "To empower businesses of all sizes to discover, compare, and choose the best 
                software tools that drive their success."
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-xl p-6 text-center">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Meet Our Team</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                <p className="text-emerald-600 text-sm mb-2">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect Tools?</h2>
            <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses who have found their ideal software solutions through ToolsMetric.
            </p>
            <Button asChild size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
              <Link to="/tools">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;